import React, { useMemo, useState } from 'react';
import {
  computeFinancialAggregates,
  getProjectRevenue,
} from '../../services/expenseFirestore';
import Modal from '../ui/Modal';
import './FinancialAnalytics.css';

function formatCurrency(n) {
  return `₹ ${Number(n || 0).toLocaleString('en-IN')}`;
}

function formatPct(n) {
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function KpiCard({ label, value, sub, growth, onClick, variant }) {
  return (
    <button
      type="button"
      className={`fa-kpi-card ${variant || ''} ${onClick ? 'is-clickable' : ''}`}
      onClick={onClick}
    >
      <span className="fa-kpi-label">{label}</span>
      <span className="fa-kpi-value">{value}</span>
      {sub && <span className="fa-kpi-sub">{sub}</span>}
      {growth !== undefined && (
        <span className={`fa-kpi-growth ${growth >= 0 ? 'positive' : 'negative'}`}>
          {formatPct(growth)} vs prior period
        </span>
      )}
    </button>
  );
}

function TrendChart({ data, label }) {
  const entries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b)).slice(-6);
  const max = Math.max(...entries.map(([, v]) => v), 1);

  return (
    <div className="fa-chart">
      <h4>{label}</h4>
      <div className="fa-chart-bars">
        {entries.length === 0 ? (
          <p className="fa-chart-empty">No data available</p>
        ) : entries.map(([key, val]) => (
          <div key={key} className="fa-chart-bar-wrap">
            <div
              className="fa-chart-bar"
              style={{ height: `${(val / max) * 100}%` }}
              title={formatCurrency(val)}
            />
            <span className="fa-chart-label">{key.slice(5) || key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FinancialAnalytics({ projects, expenses, invoices, loading }) {
  const [drillDown, setDrillDown] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);

  const agg = useMemo(
    () => computeFinancialAggregates(projects, expenses, invoices),
    [projects, expenses, invoices],
  );

  const monthlyProjects = useMemo(() => {
    if (!drillDown || drillDown.type !== 'monthly-income') return [];
    return projects.filter((p) => {
      const inv = invoices.find((i) => i.projectDocId === p.id);
      const dateStr = inv?.date || p.completedDate || p.startDate;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return key === agg.currentMonth;
    });
  }, [drillDown, projects, invoices, agg.currentMonth]);

  const monthlyExpenseRows = useMemo(() => {
    if (!drillDown || drillDown.type !== 'monthly-expenses') return [];
    return expenses.filter((e) => {
      if (!e.date) return false;
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return key === agg.currentMonth;
    });
  }, [drillDown, expenses, agg.currentMonth]);

  const detailExpenses = useMemo(
    () => (projectDetail ? expenses.filter((e) => e.projectDocId === projectDetail.id) : []),
    [projectDetail, expenses],
  );

  const categoryBreakdown = useMemo(() => {
    if (!projectDetail) return {};
    return detailExpenses.reduce((acc, e) => {
      const cat = e.expense?.trim() || 'Uncategorized';
      acc[cat] = (acc[cat] || 0) + (Number(e.amount) || 0);
      return acc;
    }, {});
  }, [projectDetail, detailExpenses]);

  if (loading) {
    return (
      <div className="expense-panel">
        <p className="expense-loading">Loading financial analytics…</p>
      </div>
    );
  }

  return (
    <div className="expense-panel fa-dashboard">
      <div className="expense-card-header">
        <h3 className="expense-panel-title">Financial Analytics</h3>
        <p className="fa-subtitle">Company-level financial visibility and trends</p>
      </div>

      <section className="fa-section">
        <h4 className="fa-section-title">Monthly Overview</h4>
        <div className="fa-kpi-grid">
          <KpiCard
            label="Monthly Income"
            value={formatCurrency(agg.monthly.income)}
            growth={agg.monthly.incomeGrowth}
            onClick={() => setDrillDown({ type: 'monthly-income', title: 'Monthly Income — Contributing Projects' })}
          />
          <KpiCard
            label="Monthly Expenses"
            value={formatCurrency(agg.monthly.expenses)}
            growth={agg.monthly.expenseGrowth}
            onClick={() => setDrillDown({ type: 'monthly-expenses', title: 'Monthly Expenses — Detail' })}
          />
          <KpiCard
            label="Monthly Profit / Loss"
            value={formatCurrency(agg.monthly.profitLoss)}
            variant={agg.monthly.profitLoss >= 0 ? 'profit' : 'loss'}
            sub="Income − Expenses"
          />
        </div>
      </section>

      <section className="fa-section">
        <h4 className="fa-section-title">Yearly Overview</h4>
        <div className="fa-kpi-grid">
          <KpiCard
            label="Yearly Income"
            value={formatCurrency(agg.yearly.income)}
            growth={agg.yearly.incomeGrowth}
          />
          <KpiCard
            label="Yearly Expenses"
            value={formatCurrency(agg.yearly.expenses)}
            growth={agg.yearly.expenseGrowth}
          />
          <KpiCard
            label="Yearly Profit / Loss"
            value={formatCurrency(agg.yearly.profitLoss)}
            variant={agg.yearly.profitLoss >= 0 ? 'profit' : 'loss'}
            sub="Income − Expenses"
          />
        </div>
      </section>

      <section className="fa-section fa-charts-row">
        <TrendChart data={agg.monthlyIncome} label="Income Trend (Monthly)" />
        <TrendChart data={agg.monthlyExpenses} label="Expense Trend (Monthly)" />
      </section>

      {agg.lossProjects.length > 0 && (
        <section className="fa-section">
          <h4 className="fa-section-title">Loss Projects</h4>
          <div className="fa-loss-list">
            {agg.lossProjects.map((p) => (
              <button
                key={p.id}
                type="button"
                className="fa-loss-item"
                onClick={() => setProjectDetail(p)}
              >
                <span className="fa-loss-name">{p.projectName}</span>
                <span className="fa-loss-amount">Loss: {formatCurrency(p.lossAmount)}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <Modal
        open={!!drillDown}
        onClose={() => setDrillDown(null)}
        title={drillDown?.title || 'Details'}
        size="lg"
      >
        {drillDown?.type === 'monthly-income' && (
          <div className="ui-data-table-wrap">
            <table>
              <thead>
                <tr><th>Project</th><th>Client</th><th>Revenue</th><th>Status</th></tr>
              </thead>
              <tbody>
                {monthlyProjects.length === 0 ? (
                  <tr><td colSpan={4} className="ui-data-table-empty">No projects this month</td></tr>
                ) : monthlyProjects.map((p) => (
                  <tr key={p.id}>
                    <td>{p.projectName}</td>
                    <td>{p.client || '—'}</td>
                    <td>{formatCurrency(getProjectRevenue(p))}</td>
                    <td>{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {drillDown?.type === 'monthly-expenses' && (
          <div className="ui-data-table-wrap">
            <table>
              <thead>
                <tr><th>Expense</th><th>Project</th><th>Amount</th><th>Date</th></tr>
              </thead>
              <tbody>
                {monthlyExpenseRows.length === 0 ? (
                  <tr><td colSpan={4} className="ui-data-table-empty">No expenses this month</td></tr>
                ) : monthlyExpenseRows.map((e) => (
                  <tr key={e.id}>
                    <td>{e.expense}</td>
                    <td>{e.projectName}</td>
                    <td>{formatCurrency(e.amount)}</td>
                    <td>{e.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal
        open={!!projectDetail}
        onClose={() => setProjectDetail(null)}
        title="Project Financial Analysis"
        size="lg"
      >
        {projectDetail && (
          <>
            <div className="fa-project-header">
              <h4>{projectDetail.projectName}</h4>
              <p>{projectDetail.projectId} · {projectDetail.client || 'No client'}</p>
            </div>
            <div className="fa-kpi-grid fa-kpi-grid-compact">
              <KpiCard label="Project Revenue" value={formatCurrency(projectDetail.revenue)} />
              <KpiCard label="Project Expenses" value={formatCurrency(projectDetail.totalExpense)} />
              <KpiCard
                label="Net Profit / Loss"
                value={formatCurrency(projectDetail.profit)}
                variant={projectDetail.profit >= 0 ? 'profit' : 'loss'}
              />
            </div>
            <h5 className="fa-detail-heading">Expense Breakdown by Category</h5>
            <ul className="fa-category-list">
              {Object.entries(categoryBreakdown).map(([cat, amt]) => (
                <li key={cat}><span>{cat}</span><strong>{formatCurrency(amt)}</strong></li>
              ))}
            </ul>
            <h5 className="fa-detail-heading">Timeline</h5>
            <p className="fa-timeline">
              {projectDetail.startDate || '—'} → {projectDetail.completedDate || 'Ongoing'}
            </p>
            <h5 className="fa-detail-heading">Root Cause Analysis</h5>
            <p className="fa-root-cause">
              {projectDetail.profit < 0
                ? `Expenses (${formatCurrency(projectDetail.totalExpense)}) exceeded revenue (${formatCurrency(projectDetail.revenue)}) by ${formatCurrency(projectDetail.lossAmount)}. Review high-cost categories above.`
                : 'Project is profitable.'}
            </p>
            <h5 className="fa-detail-heading">All Expenses</h5>
            <div className="ui-data-table-wrap">
              <table>
                <thead>
                  <tr><th>Expense</th><th>Amount</th><th>Date</th><th>Employee</th></tr>
                </thead>
                <tbody>
                  {detailExpenses.map((e) => (
                    <tr key={e.id}>
                      <td>{e.expense}</td>
                      <td>{formatCurrency(e.amount)}</td>
                      <td>{e.date}</td>
                      <td>{e.employeeName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
